/* ============================================================
   sw-smooth-scroll — in-page scrolling without an href
   ============================================================
   Any element carrying `data-sw-scroll-target` smooth-scrolls the
   page to the matching element when clicked (or on Enter/Space when
   the element is exposed as a button).

   Markup contract:
     <span data-sw-scroll-target="#reviews" role="button" tabindex="0">…</span>
     <button type="button" data-sw-scroll-target=".lpHero__list">…</button>

   The target value accepts:
     #some-id       → id selector
     .some-class    → class selector (first match wins)
     some-id        → bare name: tried as an id, then as a class
     [data-foo="1"] → any valid CSS selector

   Why not <a href="#reviews">: an anchor writes the hash into the
   address bar and pushes a history entry on click. This leaves
   `location` untouched, and always animates the scroll regardless of
   the document's `scroll-behavior`.
   ============================================================ */
(function () {
  if (window.swSmoothScrollBound) return;
  window.swSmoothScrollBound = true;

  const ATTR = 'data-sw-scroll-target';
  const BREATHING_ROOM = 16;

  function resolveTarget(value) {
    const raw = (value || '').trim();
    if (!raw) return null;

    // Explicit selector — use it as authored.
    if (/^[#.[]/.test(raw)) {
      try {
        return document.querySelector(raw);
      } catch (error) {
        return null;
      }
    }

    // Bare name — an id first, then a class of the same name.
    const byId = document.getElementById(raw);
    if (byId) return byId;

    try {
      return document.querySelector('.' + CSS.escape(raw));
    } catch (error) {
      return null;
    }
  }

  /* Pinned headers overlay the top of the viewport, so the landing
     position has to be pushed down by their height. */
  function pinnedHeaderHeight() {
    const headers = document.querySelectorAll('sticky-header, .header-wrapper, .shopify-section-group-header-group');
    let height = 0;

    headers.forEach(function (header) {
      const position = window.getComputedStyle(header).position;
      if (position !== 'sticky' && position !== 'fixed') return;
      height = Math.max(height, header.getBoundingClientRect().height);
    });

    return height;
  }

  function scrollToTarget(target) {
    const top = target.getBoundingClientRect().top + window.pageYOffset - pinnedHeaderHeight() - BREATHING_ROOM;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  }

  function triggerFrom(event) {
    if (!(event.target instanceof Element)) return null;
    return event.target.closest('[' + ATTR + ']');
  }

  document.addEventListener('click', function (event) {
    const trigger = triggerFrom(event);
    if (!trigger) return;

    const target = resolveTarget(trigger.getAttribute(ATTR));
    if (!target) return;

    event.preventDefault();
    scrollToTarget(target);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;

    const trigger = triggerFrom(event);
    if (!trigger || trigger.getAttribute('role') !== 'button') return;

    const target = resolveTarget(trigger.getAttribute(ATTR));
    if (!target) return;

    event.preventDefault();
    scrollToTarget(target);

    // Keyboard users need focus to follow the scroll.
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });
})();
